import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import PasswordRules from './components/PasswordRules';
import { mapSupabaseErrorMessage } from './authErrors';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';
import { Eye, EyeOff } from 'lucide-react';

const isCompanyEmail = (email: string): boolean => {
  const domain = email.toLowerCase().trim().split('@')[1];
  if (!domain) return false;

  const personalEmailDomains = ['gmail.com','yahoo.com','outlook.com','icloud.com','hotmail.com','aol.com'];
  const tempEmailDomains = ['mailinator.com', 'guerrillamail.com', '10minutemail.com', 'tempmail.com', 'yopmail.com', 'fakeinbox.com', 'trashmail.com', 'sharklasers.com', 'throwaway.email', 'temp-mail.org'];
  return !([...personalEmailDomains, ...tempEmailDomains].some(domainName => domain === domainName || domain.endsWith(`.${domainName}`)));
};

const isValidPhoneNumber = (value: string): boolean => {
  if (!value) return false;
  const normalized = value.trim();
  return /^[+]?[\d\s().-]{7,20}$/.test(normalized);
};

const signupSchema = z.object({
  firstName: z
    .string()
    .min(1, 'Please enter your first name')
    .refine((val) => val.trim().length > 0, 'Please enter your first name'),
  lastName: z
    .string()
    .min(1, 'Please enter your last name')
    .refine((val) => val.trim().length > 0, 'Please enter your last name'),
  role: z
    .string()
    .min(1, 'Please enter your role')
    .refine((val) => val.trim().length > 0, 'Please enter your role'),
  phoneNumber: z
    .string()
    .min(1, 'Please enter your phone number')
    .refine((val) => isValidPhoneNumber(val), 'Please enter a valid phone number'),
  email: z
    .email('Please enter a valid email address')
    .refine((val) => isCompanyEmail(val), 'Please use a company email'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .refine((val) => /[A-Z]/.test(val), 'Password must contain at least one uppercase letter')
    .refine((val) => /\d/.test(val), 'Password must contain at least one number')
    .refine((val) => /[^A-Za-z0-9]/.test(val), 'Password must contain at least one special character'),
  confirmPassword: z
    .string()
    .min(1, 'Please confirm your password'),
  accepted: z
    .boolean()
    .refine((val) => val === true, 'Please accept Terms & Privacy'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type SignupFormData = z.infer<typeof signupSchema>;

const SignupPage: React.FC = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Preserve invitation_token throughout the signup process
  // Store it in a ref so it persists even if localStorage is cleared
  const invitationTokenRef = useRef<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      role: '',
      phoneNumber: '',
      email: '',
      password: '',
      confirmPassword: '',
      accepted: false,
    },
  });

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  const rules = useMemo(() => ({
    length: password ? password.length >= 8 : false,
    uppercase: password ? /[A-Z]/.test(password) : false,
    number: password ? /\d/.test(password) : false,
    special: password ? /[^A-Za-z0-9]/.test(password) : false,
    match: confirmPassword ? password === confirmPassword : false,
  }), [password, confirmPassword]);

  useEffect(() => {
    // On mount, save the invitation_token to a ref for safekeeping
    const token = localStorage.getItem('invitation_token');
    if (token) {
      invitationTokenRef.current = token;
    }

    // Monitor localStorage and restore token if it gets cleared
    const checkInterval = setInterval(() => {
      const currentToken = localStorage.getItem('invitation_token');
      if (!currentToken && invitationTokenRef.current) {
        // Token was cleared, restore it
        localStorage.setItem('invitation_token', invitationTokenRef.current);
      }
    }, 500); // Check every 500ms

    return () => {
      clearInterval(checkInterval);
    };
  }, []);

  const onSubmit = async (data: SignupFormData) => {
    const trimmedFirstName = data.firstName.trim();
    const trimmedLastName = data.lastName.trim();
    const trimmedRole = data.role.trim();
    const trimmedPhoneNumber = data.phoneNumber.trim();
    const trimmedEmail = data.email.trim();

    try {
      // IMPORTANT: Preserve invitation_token before signup
      // It must persist through signup and only be removed after successful login
      // Use ref value as fallback in case localStorage was cleared
      const invitationToken = localStorage.getItem('invitation_token') || invitationTokenRef.current;

      const { error: signUpError } = await supabase.auth.signUp({
        email: trimmedEmail,
        password: data.password,
        options: {
          data: {
            first_name: trimmedFirstName,
            last_name: trimmedLastName,
            role: trimmedRole,
            phone: trimmedPhoneNumber,
          },
        },
      });

      if (signUpError) {
        const msg = signUpError.message?.toLowerCase() ?? '';
        if (
          msg.includes('already registered') ||
          msg.includes('already exists') ||
          msg.includes('duplicate') ||
          msg.includes('user exists')
        ) {
          toast.error('An account with this email already exists. Please sign in instead.');
          return;
        }
        throw signUpError;
      }

      // Restore invitation_token if it was cleared during signup
      // This ensures it persists through signup and is available during login
      // Use ref value as fallback
      const tokenToRestore = invitationToken || invitationTokenRef.current;
      if (tokenToRestore) {
        localStorage.setItem('invitation_token', tokenToRestore);
        invitationTokenRef.current = tokenToRestore; // Update ref
      }

      toast.success('Check your email for verification');
      reset();
      navigate('/login');
    } catch (err: any) {
      toast.error(mapSupabaseErrorMessage(err?.message) || 'Sign up failed');
    }
  };

  return (
    <div className="eg-auth-page auth-page">
      <AnimatedBackground />
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div className="eg-auth-card">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light tracking-wide" style={{ color: 'var(--color-auth-text)' }}>Sign Up</h2>
          </div>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <input
                type="text"
                placeholder="First name"
                {...register('firstName')}
                className={`eg-auth-input ${errors.firstName ? 'input-error' : ''}`}
              />
              {errors.firstName && (
                <p className="mt-1 text-sm text-red-400">{errors.firstName.message}</p>
              )}
            </div>
            <div>
              <input
                type="text"
                placeholder="Last name"
                {...register('lastName')}
                className={`eg-auth-input ${errors.lastName ? 'input-error' : ''}`}
              />
              {errors.lastName && (
                <p className="mt-1 text-sm text-red-400">{errors.lastName.message}</p>
              )}
            </div>
          </div>
          <div>
            <input
              type="text"
              placeholder="Role"
              {...register('role')}
              className={`eg-auth-input ${errors.role ? 'input-error' : ''}`}
            />
            {errors.role && (
              <p className="mt-1 text-sm text-red-400">{errors.role.message}</p>
            )}
          </div>
          <div>
            <input
              type="tel"
              placeholder="Phone number"
              {...register('phoneNumber')}
              className={`eg-auth-input ${errors.phoneNumber ? 'input-error' : ''}`}
            />
            {errors.phoneNumber && (
              <p className="mt-1 text-sm text-red-400">{errors.phoneNumber.message}</p>
            )}
          </div>
          <div>
            <input
              type="email"
              placeholder="Company email"
              {...register('email')}
              className={`eg-auth-input ${errors.email ? 'input-error' : ''}`}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
            )}
          </div>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              {...register('password')}
              className={`eg-auth-input pr-10 ${errors.password ? 'input-error' : ''}`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors focus:outline-none"
              style={{ color: 'var(--color-auth-eye-icon)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon)'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-400">{errors.password.message}</p>
          )}
          <div className="relative">
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm password"
              {...register('confirmPassword')}
              className={`eg-auth-input pr-10 ${errors.confirmPassword ? 'input-error' : ''}`}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors focus:outline-none"
              style={{ color: 'var(--color-auth-eye-icon)' }}
              onMouseEnter={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon-hover)'}
              onMouseLeave={(e) => e.currentTarget.style.color = 'var(--color-auth-eye-icon)'}
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="text-sm text-red-400">{errors.confirmPassword.message}</p>
          )}
          <PasswordRules rules={rules} />
          <div>
            <label className="flex items-start space-x-2 text-sm" style={{ color: 'var(--color-auth-text-secondary)' }}>
              <input
                type="checkbox"
                {...register('accepted')}
                className="mt-1 accent-[#17c480] focus:ring-2 focus:ring-[#17c480] rounded"
              />
              <span>
                I accept the{' '}
                <a
                  href="https://legal.engramiq.com/terms-of-service"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] rounded"
                >
                  Terms of Service
                </a>
                {' '}and{' '}
                <a
                  href="https://legal.engramiq.com/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] rounded"
                >
                  Privacy Policy
                </a>
              </span>
            </label>
            {errors.accepted && (
              <p className="mt-1 text-sm text-red-400">{errors.accepted.message}</p>
            )}
          </div>
          <button
            type="submit"
            disabled={isSubmitting}
            className="eg-auth-btn"
          >
            {isSubmitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>
        <div className="mt-3 text-sm text-center" style={{ color: 'var(--color-auth-text-secondary)' }}>
          <span>
            Have an account?{' '}
            <Link to="/login" className="inline-block py-1 underline hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] rounded" style={{ color: 'var(--color-auth-link)' }}>Login</Link>
          </span>
        </div>
        <div className="mt-4 text-xs text-center" style={{ color: 'var(--color-auth-text-muted)' }}>
          <p>Need help? Contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a></p>
        </div>
        </div>
      </div>
      {/* <SupportChat /> */}
    </div>
  );
};

export default SignupPage;
